import React, { useCallback, useEffect, useState } from 'react';
import DateField from '../../components/Shared/DateField';
import {
  listMandatari, createMandatar, updateMandatar, deleteMandatar,
  listMandatarInstructiuni, createMandatarInstructiune, updateMandatarInstructiune,
  deleteMandatarInstructiune, uploadMandatarInstructiuneDoc,
} from '../../services/mandatari';
import { listAllProceduri } from '../../services/proceduri';
import { listStaffUsers } from '../../services/auth';
import { FilterPill, FilterClearBtn, FilterCountHint } from '../../components/Shared/FilterPill';
import '../Clienti/ClientiPage.css';
import './MandatariPage.css';

/* ── Constants ── */
const TIP_OPTS    = [{ v: 'cabinet', l: 'Cabinet' }, { v: 'persoana_fizica', l: 'Persoana fizica' }, { v: 'firma', l: 'Firma' }];
const STATUS_OPTS = [{ v: 'activ', l: 'Activ' }, { v: 'inactiv', l: 'Inactiv' }, { v: 'suspendat', l: 'Suspendat' }];
const NIVEL_OPTS  = ['Incepator', 'Intermediar', 'Avansat', 'Expert'];
const MONEDA_OPTS = ['EUR', 'USD', 'GBP', 'RON', 'CHF'];
const TABS        = ['Date generale', 'Contacte', 'Jurisdictii', 'Domenii PI', 'Tarife', 'Dosare', 'Instructiuni', 'Istoric'];

const EMPTY = {
  nume: '', tip: 'cabinet', tara: '', adresa: '', email: '',
  data_inceput: '', status: 'activ', observatii: '',
  contacte: [], jurisdictii: [], domenii: [], tarife: [], proceduri_ids: [], istoric: [],
};
const EMPTY_CONTACT     = { nume: '', functie: '', email: '', telefon: '', limba: '' };
const EMPTY_JURISDICTIE = { oficiu: '', nr_acreditare: '', data_valabilitate: '' };
const EMPTY_DOMENIU     = { tip_pi: '', nivel_experienta: 'Avansat' };
const EMPTY_TARIF       = { tip_serviciu: '', tarif: '', moneda: 'EUR', data_actualizare: '' };
const EMPTY_ISTORIC     = { data: '', tip: '', descriere: '', utilizator: '' };

function statusClass(s) {
  if (s === 'activ')   return 'mnd-status-badge mnd-status-activ';
  if (s === 'inactiv') return 'mnd-status-badge mnd-status-inactiv';
  return 'mnd-status-badge mnd-status-suspendat';
}

/* ── Toast ── */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg) => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

/* ── Modal ── */
function MandatarModal({ mandatar, allProceduri, staffUsers, onSave, onDelete, onClose, saving, deleting, showToast }) {
  const isNew = !mandatar?.id;
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(isNew ? { ...EMPTY } : { ...mandatar });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addRow   = (k, empty) => set(k, [...(form[k] || []), { ...empty }]);
  const delRow   = (k, i)    => set(k, form[k].filter((_, idx) => idx !== i));
  const patchRow = (k, i, patch) =>
    set(k, form[k].map((row, idx) => idx === i ? { ...row, ...patch } : row));

  const linkedProcs    = (form.proceduri_ids || []).map(id => allProceduri.find(p => p.id === id)).filter(Boolean);
  const availableProcs = allProceduri.filter(p => !(form.proceduri_ids || []).includes(p.id));

  // ── Instructiuni (Phase 9) — persisted independently of the main form save ──
  const [instructiuni, setInstructiuni] = useState([]);
  const [instrLoading, setInstrLoading] = useState(false);
  const [newInstr, setNewInstr] = useState({ procedura_id: '', continut: '', data_trimitere: '', termen_confirmare_asteptat: '' });
  const [savingInstr, setSavingInstr] = useState(false);

  const loadInstructiuni = useCallback(() => {
    if (!mandatar?.id) return;
    setInstrLoading(true);
    listMandatarInstructiuni(mandatar.id)
      .then(res => setInstructiuni(res.instructiuni || []))
      .catch(() => showToast && showToast('Eroare la incarcarea instructiunilor'))
      .finally(() => setInstrLoading(false));
  }, [mandatar?.id, showToast]);

  useEffect(() => { loadInstructiuni(); }, [loadInstructiuni]);

  const handleAddInstr = async () => {
    if (!newInstr.continut.trim() || !newInstr.data_trimitere) {
      showToast && showToast('Continutul si data trimiterii sunt obligatorii');
      return;
    }
    setSavingInstr(true);
    try {
      await createMandatarInstructiune(mandatar.id, {
        procedura_id: newInstr.procedura_id || null,
        continut: newInstr.continut.trim(),
        data_trimitere: newInstr.data_trimitere,
        termen_confirmare_asteptat: newInstr.termen_confirmare_asteptat || null,
      });
      setNewInstr({ procedura_id: '', continut: '', data_trimitere: '', termen_confirmare_asteptat: '' });
      loadInstructiuni();
      showToast && showToast('Instructiune inregistrata');
    } catch {
      showToast && showToast('Eroare la salvarea instructiunii');
    } finally {
      setSavingInstr(false);
    }
  };

  const markConfirmarePrimire = async (instr) => {
    const today = new Date().toISOString().slice(0, 10);
    try {
      await updateMandatarInstructiune(instr.id, { data_confirmare_primire: today });
      loadInstructiuni();
    } catch {
      showToast && showToast('Eroare la actualizare');
    }
  };

  const handleUploadProof = async (instr, file) => {
    if (!file) return;
    try {
      await uploadMandatarInstructiuneDoc(instr.id, file);
      loadInstructiuni();
      showToast && showToast('Document atasat — depunere confirmata');
    } catch {
      showToast && showToast('Eroare la incarcarea documentului');
    }
  };

  const handleDeleteInstr = async (instr) => {
    if (!window.confirm('Stergi aceasta instructiune?')) return;
    try {
      await deleteMandatarInstructiune(instr.id);
      loadInstructiuni();
    } catch {
      showToast && showToast('Eroare la stergere');
    }
  };

  const INSTR_STATUS_LABEL = {
    instruita_neconfirmata: 'Instruita — neconfirmata',
    primire_confirmata: 'Primire confirmata',
    depunere_confirmata: 'Depunere confirmata',
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box mnd-modal" style={{ maxWidth: 820 }} onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="modal-sticky-header">
          <div className="cj-header" style={{ marginBottom: 0 }}>
            <div className="cj-left">
              <div className="cj-name">{isNew ? 'Mandatar nou' : (form.nume || 'Mandatar')}</div>
              {!isNew && form.tara && (
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{form.tara}</div>
              )}
            </div>
            <div className="cj-right">
              <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>✕</button>
            </div>
          </div>
          <div className="modal-tabs">
            {TABS.map((t, i) => (
              <button key={t} type="button" className={`modal-tab${tab === i ? ' mt-active' : ''}`} onClick={() => setTab(i)}>
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="modal-scroll-body">

          {/* Tab 0 — Date generale */}
          {tab === 0 && (
            <>
              <div className="f-row mb10">
                <div className="field" style={{ gridColumn: 'span 2' }}>
                  <label>Nume / Denumire</label>
                  <input value={form.nume} onChange={e => set('nume', e.target.value)} placeholder="Nume mandatar..." autoFocus={isNew} />
                </div>
              </div>
              <div className="f-row f-row-3 mb10">
                <div className="field">
                  <label>Tip</label>
                  <FilterPill value={form.tip} onChange={e => set('tip', e.target.value)}>
                    <option value="">Tip</option>
                    {TIP_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </FilterPill>
                </div>
                <div className="field">
                  <label>Status</label>
                  <FilterPill value={form.status} onChange={e => set('status', e.target.value)}>
                    <option value="">Status</option>
                    {STATUS_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </FilterPill>
                </div>
                <div className="field">
                  <label>Data inceput colaborare</label>
                  <DateField value={form.data_inceput || ''} onChange={v => set('data_inceput', v)} />
                </div>
              </div>
              <div className="f-row mb10">
                <div className="field">
                  <label>Tara</label>
                  <input value={form.tara} onChange={e => set('tara', e.target.value)} placeholder="ex: Romania, Franta..." />
                </div>
                <div className="field">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@exemplu.com" />
                </div>
              </div>
              <div className="field mb10">
                <label>Adresa</label>
                <input value={form.adresa} onChange={e => set('adresa', e.target.value)} placeholder="Adresa completa..." />
              </div>
              <div className="field mb10">
                <label>Observatii</label>
                <textarea value={form.observatii} onChange={e => set('observatii', e.target.value)} rows={3} placeholder="Note interne..." />
              </div>
            </>
          )}

          {/* Tab 1 — Contacte */}
          {tab === 1 && (
            <>
              <div className="section-label">Persoane de contact</div>
              {(form.contacte || []).map((c, i) => (
                <div key={i} className="mnd-subrow">
                  <button className="mnd-subrow-del" type="button" onClick={() => delRow('contacte', i)}>✕</button>
                  <div className="f-row mb10">
                    <div className="field">
                      <label>Nume</label>
                      <input value={c.nume} onChange={e => patchRow('contacte', i, { nume: e.target.value })} placeholder="Nume complet" />
                    </div>
                    <div className="field">
                      <label>Functie / Rol</label>
                      <input value={c.functie} onChange={e => patchRow('contacte', i, { functie: e.target.value })} placeholder="ex: Director, Avocat..." />
                    </div>
                  </div>
                  <div className="f-row f-row-3">
                    <div className="field">
                      <label>Email</label>
                      <input type="email" value={c.email} onChange={e => patchRow('contacte', i, { email: e.target.value })} placeholder="email@..." />
                    </div>
                    <div className="field">
                      <label>Telefon</label>
                      <input value={c.telefon} onChange={e => patchRow('contacte', i, { telefon: e.target.value })} placeholder="+40..." />
                    </div>
                    <div className="field">
                      <label>Limba preferata</label>
                      <input value={c.limba} onChange={e => patchRow('contacte', i, { limba: e.target.value })} placeholder="ex: Romana, Engleza..." />
                    </div>
                  </div>
                </div>
              ))}
              <button className="mnd-add-btn" type="button" onClick={() => addRow('contacte', EMPTY_CONTACT)}>
                + Adauga persoana de contact
              </button>
            </>
          )}

          {/* Tab 2 — Jurisdictii */}
          {tab === 2 && (
            <>
              <div className="section-label">Jurisdictii / Acreditari</div>
              {(form.jurisdictii || []).map((j, i) => (
                <div key={i} className="mnd-subrow">
                  <button className="mnd-subrow-del" type="button" onClick={() => delRow('jurisdictii', i)}>✕</button>
                  <div className="f-row f-row-3">
                    <div className="field">
                      <label>Oficiu</label>
                      <input value={j.oficiu} onChange={e => patchRow('jurisdictii', i, { oficiu: e.target.value })} placeholder="ex: OSIM, EUIPO, USPTO..." />
                    </div>
                    <div className="field">
                      <label>Nr. acreditare</label>
                      <input value={j.nr_acreditare} onChange={e => patchRow('jurisdictii', i, { nr_acreditare: e.target.value })} placeholder="Numar certificat" />
                    </div>
                    <div className="field">
                      <label>Valabil pana la</label>
                      <DateField value={j.data_valabilitate} onChange={v => patchRow('jurisdictii', i, { data_valabilitate: v })} />
                    </div>
                  </div>
                </div>
              ))}
              <button className="mnd-add-btn" type="button" onClick={() => addRow('jurisdictii', EMPTY_JURISDICTIE)}>
                + Adauga jurisdictie
              </button>
            </>
          )}

          {/* Tab 3 — Domenii PI */}
          {tab === 3 && (
            <>
              <div className="section-label">Domenii de proprietate intelectuala</div>
              {(form.domenii || []).map((d, i) => (
                <div key={i} className="mnd-subrow">
                  <button className="mnd-subrow-del" type="button" onClick={() => delRow('domenii', i)}>✕</button>
                  <div className="f-row">
                    <div className="field">
                      <label>Tip PI</label>
                      <input value={d.tip_pi} onChange={e => patchRow('domenii', i, { tip_pi: e.target.value })} placeholder="ex: Marca, Brevet, Design..." />
                    </div>
                    <div className="field">
                      <label>Nivel experienta</label>
                      <FilterPill value={d.nivel_experienta} onChange={e => patchRow('domenii', i, { nivel_experienta: e.target.value })}>
                        <option value="">Nivel</option>
                        {NIVEL_OPTS.map(n => <option key={n} value={n}>{n}</option>)}
                      </FilterPill>
                    </div>
                  </div>
                </div>
              ))}
              <button className="mnd-add-btn" type="button" onClick={() => addRow('domenii', EMPTY_DOMENIU)}>
                + Adauga domeniu
              </button>
            </>
          )}

          {/* Tab 4 — Tarife */}
          {tab === 4 && (
            <>
              <div className="section-label">Tarife</div>
              {(form.tarife || []).map((t, i) => (
                <div key={i} className="mnd-subrow">
                  <button className="mnd-subrow-del" type="button" onClick={() => delRow('tarife', i)}>✕</button>
                  <div className="f-row f-row-3 mb10">
                    <div className="field" style={{ gridColumn: 'span 2' }}>
                      <label>Tip serviciu</label>
                      <input value={t.tip_serviciu} onChange={e => patchRow('tarife', i, { tip_serviciu: e.target.value })} placeholder="ex: Depunere marca, Opozitie..." />
                    </div>
                    <div className="field">
                      <label>Data actualizare</label>
                      <DateField value={t.data_actualizare} onChange={v => patchRow('tarife', i, { data_actualizare: v })} />
                    </div>
                  </div>
                  <div className="f-row">
                    <div className="field">
                      <label>Tarif</label>
                      <input type="number" value={t.tarif} onChange={e => patchRow('tarife', i, { tarif: e.target.value })} placeholder="0" />
                    </div>
                    <div className="field">
                      <label>Moneda</label>
                      <FilterPill value={t.moneda} onChange={e => patchRow('tarife', i, { moneda: e.target.value })}>
                        <option value="">Moneda</option>
                        {MONEDA_OPTS.map(m => <option key={m} value={m}>{m}</option>)}
                      </FilterPill>
                    </div>
                  </div>
                </div>
              ))}
              <button className="mnd-add-btn" type="button" onClick={() => addRow('tarife', EMPTY_TARIF)}>
                + Adauga tarif
              </button>
            </>
          )}

          {/* Tab 5 — Dosare */}
          {tab === 5 && (
            <>
              <div className="section-label" style={{ marginBottom: 10 }}>Dosare asociate</div>
              {availableProcs.length > 0 && (
                <div className="field mb10">
                  <label>Adauga dosar</label>
                  <FilterPill searchable value="" onChange={e => {
                    const id = parseInt(e.target.value, 10);
                    if (id) set('proceduri_ids', [...(form.proceduri_ids || []), id]);
                  }}>
                    <option value="">— Selecteaza dosar —</option>
                    {availableProcs.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.client_name} — {p.data?.denumire || p.data?.numar || p.tip_proc}
                      </option>
                    ))}
                  </FilterPill>
                </div>
              )}
              {linkedProcs.length === 0 && <p className="f-hint">Niciun dosar asociat.</p>}
              {linkedProcs.map(p => (
                <div key={p.id} className="mnd-proc-row">
                  <div className="mnd-proc-info">
                    <span className="mnd-proc-name">{p.data?.denumire || p.data?.numar || p.tip_proc}</span>
                    <span className="mnd-proc-sub">{p.client_name} · {p.tip_proc}</span>
                  </div>
                  <button className="btn btn-ghost btn-xs" type="button"
                    onClick={() => set('proceduri_ids', form.proceduri_ids.filter(id => id !== p.id))}>✕</button>
                </div>
              ))}
            </>
          )}

          {/* Tab 6 — Instructiuni (Phase 9) */}
          {tab === 6 && (
            <>
              <div className="section-label" style={{ marginBottom: 10 }}>Instructiuni trimise</div>

              {isNew ? (
                <p className="f-hint">Salveaza mai intai mandatarul pentru a putea inregistra instructiuni.</p>
              ) : (
                <>
                  <div className="mnd-subrow" style={{ marginBottom: 16 }}>
                    <div className="f-row f-row-3 mb10">
                      <div className="field">
                        <label>Dosar (optional)</label>
                        <FilterPill searchable value={newInstr.procedura_id} onChange={e => setNewInstr(f => ({ ...f, procedura_id: e.target.value }))}>
                          <option value="">— Fara dosar specific —</option>
                          {linkedProcs.map(p => (
                            <option key={p.id} value={p.id}>{p.client_name} — {p.data?.denumire || p.data?.numar || p.tip_proc}</option>
                          ))}
                        </FilterPill>
                      </div>
                      <div className="field">
                        <label>Data trimiterii</label>
                        <DateField value={newInstr.data_trimitere} onChange={v => setNewInstr(f => ({ ...f, data_trimitere: v }))} />
                      </div>
                      <div className="field">
                        <label>Termen confirmare asteptat</label>
                        <DateField value={newInstr.termen_confirmare_asteptat} onChange={v => setNewInstr(f => ({ ...f, termen_confirmare_asteptat: v }))} />
                      </div>
                    </div>
                    <div className="field mb10">
                      <label>Continut instructiune</label>
                      <textarea
                        value={newInstr.continut}
                        onChange={e => setNewInstr(f => ({ ...f, continut: e.target.value }))}
                        rows={2}
                        placeholder="Actiunea ceruta, clase, termen limita cunoscut, plafon onorariu, documente atasate..."
                      />
                    </div>
                    <button className="btn btn-primary btn-sm" type="button" onClick={handleAddInstr} disabled={savingInstr}>
                      {savingInstr ? 'Se salveaza...' : '+ Inregistreaza instructiune'}
                    </button>
                  </div>

                  {instrLoading ? (
                    <p className="f-hint">Se incarca...</p>
                  ) : instructiuni.length === 0 ? (
                    <p className="f-hint">Nicio instructiune inregistrata inca.</p>
                  ) : (
                    instructiuni.map(instr => (
                      <div key={instr.id} className="mnd-subrow">
                        <button className="mnd-subrow-del" type="button" onClick={() => handleDeleteInstr(instr)}>✕</button>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>
                              {instr.client_name ? `${instr.client_name} — ` : ''}
                              Trimisa {instr.data_trimitere} {instr.trimis_de_nume ? `de ${instr.trimis_de_nume}` : ''}
                            </div>
                            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>{instr.continut}</div>
                          </div>
                          <span className={`mnd-status-badge ${instr.status === 'depunere_confirmata' ? 'mnd-status-activ' : instr.status === 'primire_confirmata' ? '' : 'mnd-status-suspendat'}`}>
                            {INSTR_STATUS_LABEL[instr.status] || instr.status}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                          {!instr.data_confirmare_primire && (
                            <button className="btn btn-secondary btn-xs" type="button" onClick={() => markConfirmarePrimire(instr)}>
                              Marcheaza confirmare primire
                            </button>
                          )}
                          {!instr.data_confirmare_depunere && (
                            <label className="btn btn-secondary btn-xs" style={{ cursor: 'pointer' }}>
                              Ataseaza dovada depunere
                              <input type="file" style={{ display: 'none' }} onChange={e => handleUploadProof(instr, e.target.files[0])} />
                            </label>
                          )}
                          {instr.document_dovada_url && (
                            <a className="btn btn-ghost btn-xs" href={instr.document_dovada_url} target="_blank" rel="noreferrer">Vezi dovada</a>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </>
              )}
            </>
          )}

          {/* Tab 7 — Istoric */}
          {tab === 7 && (
            <>
              <div className="section-label">Istoric</div>
              {(form.istoric || []).map((h, i) => (
                <div key={i} className="mnd-subrow">
                  <button className="mnd-subrow-del" type="button" onClick={() => delRow('istoric', i)}>✕</button>
                  <div className="f-row mb10">
                    <div className="field">
                      <label>Data</label>
                      <DateField value={h.data} onChange={v => patchRow('istoric', i, { data: v })} />
                    </div>
                    <div className="field">
                      <label>Tip eveniment</label>
                      <input value={h.tip} onChange={e => patchRow('istoric', i, { tip: e.target.value })} placeholder="ex: Intalnire, Email, Negociere..." />
                    </div>
                  </div>
                  <div className="field mb10">
                    <label>Descriere</label>
                    <textarea value={h.descriere} onChange={e => patchRow('istoric', i, { descriere: e.target.value })} rows={2} placeholder="Detalii..." />
                  </div>
                  <div className="field">
                    <label>Utilizator</label>
                    <FilterPill searchable value={h.utilizator} onChange={e => patchRow('istoric', i, { utilizator: e.target.value })}>
                      <option value="">— Selecteaza utilizator —</option>
                      {staffUsers.map(u => <option key={u.id} value={u.full_name}>{u.full_name}</option>)}
                    </FilterPill>
                  </div>
                </div>
              ))}
              <button className="mnd-add-btn" type="button" onClick={() => addRow('istoric', EMPTY_ISTORIC)}>
                + Adauga intrare
              </button>
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="fm-footer" style={{ justifyContent: 'space-between' }}>
          <div>
            {!isNew && (
              <button className="btn btn-ghost btn-sm" type="button" disabled={deleting}
                style={{ color: '#ef4444' }} onClick={onDelete}>
                {deleting ? 'Se sterge...' : 'Sterge mandatar'}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>Anuleaza</button>
            <button className="btn btn-primary btn-sm" type="button"
              disabled={saving || !form.nume.trim()} onClick={() => onSave(form)}>
              {saving ? 'Se salveaza...' : isNew ? 'Creeaza' : 'Salveaza'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── Page ─────────────────────── */
export default function MandatariPage() {
  const [mandatari, setMandatari]       = useState([]);
  const [allProceduri, setAllProceduri] = useState([]);
  const [staffUsers, setStaffUsers]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [modal, setModal]               = useState(undefined);
  const [saving, setSaving]             = useState(false);
  const [deleting, setDeleting]         = useState(false);

  const [search, setSearch]             = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTara, setFilterTara]     = useState('');

  const { toasts, show: showToast } = useToast();

  useEffect(() => {
    Promise.all([listMandatari(), listAllProceduri([]), listStaffUsers()])
      .then(([mData, pData, uData]) => {
        setMandatari(mData.mandatari || []);
        setAllProceduri(pData.proceduri || []);
        setStaffUsers(uData.users || []);
      })
      .catch(() => showToast('Eroare la incarcare'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const filtered = mandatari.filter(m => {
    if (search && !m.nume.toLowerCase().includes(search.toLowerCase()) &&
        !(m.tara || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (filterStatus && m.status !== filterStatus) return false;
    if (filterTara   && m.tara?.toLowerCase() !== filterTara.toLowerCase()) return false;
    return true;
  });

  const uniqueTari = [...new Set(mandatari.map(m => m.tara).filter(Boolean))].sort();
  const hasFilters = filterStatus || filterTara || search;

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (modal?.id) {
        const res = await updateMandatar(modal.id, form);
        setMandatari(prev => prev.map(m => m.id === modal.id ? res.mandatar : m));
        showToast('✓ Mandatar actualizat');
      } else {
        const res = await createMandatar(form);
        setMandatari(prev => [...prev, res.mandatar].sort((a, b) => a.nume.localeCompare(b.nume)));
        showToast('✓ Mandatar creat');
      }
      setModal(undefined);
    } catch {
      showToast('Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!modal?.id || !window.confirm(`Stergi mandatarul "${modal.nume}"?`)) return;
    setDeleting(true);
    try {
      await deleteMandatar(modal.id);
      setMandatari(prev => prev.filter(m => m.id !== modal.id));
      setModal(undefined);
      showToast('Mandatar sters');
    } catch {
      showToast('Eroare la stergere');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <main className="cp-main">
      <div className="topbar">
        <div className="topbar-left">
          <h1 className="page-title">Mandatari Externi</h1>
          {mandatari.length > 0 && <span className="count-badge">{mandatari.length}</span>}
        </div>
        <div className="topbar-right">
          <button className="btn btn-primary btn-sm" type="button" onClick={() => setModal(null)}>
            + Mandatar nou
          </button>
        </div>
      </div>

      <div className="content">
        {/* Filters — using the same filter-bar + FilterPill pattern as OficiuPage / TermenePage */}
        <div className="filter-bar">
          <div className="search-wrap">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input className="search-input" placeholder="Cauta dupa nume sau tara..."
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          <FilterPill value={filterTara} onChange={e => setFilterTara(e.target.value)}>
            <option value="">Toate tarile</option>
            {uniqueTari.map(t => <option key={t} value={t}>{t}</option>)}
          </FilterPill>

          <FilterPill value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">Toate statusurile</option>
            {STATUS_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </FilterPill>

          {hasFilters && (
            <FilterClearBtn onClick={() => { setSearch(''); setFilterStatus(''); setFilterTara(''); }} />
          )}
          <FilterCountHint count={filtered.length} singular="mandatar" plural="mandatari" />
        </div>

        {/* Table */}
        <div className="clients-card">
          <table className="clients-table">
            <thead>
              <tr>
                <th>Nume</th>
                <th>Tip</th>
                <th>Tara</th>
                <th>Jurisdictii</th>
                <th>Dosare</th>
                <th>Instructiuni deschise</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--muted)', padding: '24px 0' }}>Se incarca...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state">
                      <div className="empty-icon">🤝</div>
                      <div className="empty-text">
                        {mandatari.length === 0
                          ? 'Niciun mandatar inregistrat. Apasa "+ Mandatar nou" pentru a incepe.'
                          : 'Niciun mandatar gasit pentru filtrele selectate.'}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              {filtered.map(m => (
                <tr key={m.id} onClick={() => setModal(m)}>
                  <td>
                    <div className="client-name-cell">
                      <div className="cn-name">{m.nume}</div>
                      {m.email && <div className="cn-sub">{m.email}</div>}
                    </div>
                  </td>
                  <td>{TIP_OPTS.find(o => o.v === m.tip)?.l || m.tip}</td>
                  <td>{m.tara || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                  <td>
                    {m.jurisdictii?.length > 0
                      ? m.jurisdictii.map(j => j.oficiu).filter(Boolean).join(', ')
                      : <span style={{ color: 'var(--muted)' }}>—</span>}
                  </td>
                  <td className="num-cell">
                    {m.proceduri_ids?.length > 0
                      ? <>{m.proceduri_ids.length} <span>dosare</span></>
                      : <span style={{ color: 'var(--muted)' }}>—</span>}
                  </td>
                  <td className="num-cell">
                    {m.instructiuni_deschise > 0
                      ? <span style={{ color: '#92400e', fontWeight: 600 }}>{m.instructiuni_deschise}</span>
                      : <span style={{ color: 'var(--muted)' }}>—</span>}
                  </td>
                  <td>
                    <span className={statusClass(m.status)}>
                      {STATUS_OPTS.find(o => o.v === m.status)?.l || m.status}
                    </span>
                    {m.inactiv_24_luni && (
                      <span className="mnd-status-badge mnd-status-suspendat" style={{ marginLeft: 6 }} title="Nicio activitate in ultimele 24 de luni">
                        Inactiv 24 luni
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal !== undefined && (
        <MandatarModal
          mandatar={modal}
          allProceduri={allProceduri}
          staffUsers={staffUsers}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setModal(undefined)}
          saving={saving}
          deleting={deleting}
          showToast={showToast}
        />
      )}

      <div className="toast-wrap">
        {toasts.map(t => <div key={t.id} className="toast">{t.msg}</div>)}
      </div>
    </main>
  );
}
